import { ReactNode } from 'react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';

import { FormatNumber } from 'utils/helpers';

type Tom = 'amarelo' | 'lilas';

const COR_DA_ETIQUETA: Record<Tom, string> = {
  amarelo: 'bg-brand-yellow-light',
  lilas: 'bg-brand-purple-light',
};

interface CartaoDoExtratoProps {
  etiqueta?: string;
  tom?: Tom;
  /** Data mostrada ao lado da etiqueta (dd/MM/yyyy). */
  data?: number;
  /** Data em caixa à esquerda (dia e mês), como na linha do tempo das realizações. */
  dataEmCaixa?: number;
  /** Linha acima do título: fase, tipo de crédito. */
  contexto?: ReactNode;
  titulo?: ReactNode;
  /** Chips extras abaixo do título (comportamento, moeda, avisos). */
  extras?: ReactNode;
  participacao?: number;
  /** Sem pontos (`undefined`), a pílula não aparece. */
  pontos?: number;
  /** Pontos em bloco ("2.500 / PONTOS") em vez de pílula ("200 pontos"). */
  pontosEmBloco?: boolean;
  /** Conteúdo expandido pelo "mais" do título. */
  detalhes?: ReactNode;
}

function CaixaDeData({ time }: { time: number }) {
  const data = new Date(time);

  return (
    <div className="flex w-16 shrink-0 flex-col items-center gap-1 rounded-xl bg-brand-purple-light px-1.5 py-2.5">
      <span className="font-display text-lg font-extrabold leading-none text-brand-blue-dark">{format(data, 'dd')}</span>
      <span className="text-[10px] uppercase tracking-[0.06em] text-content">
        {format(data, 'MMM', { locale: ptBR }).replace('.', '')}
      </span>
    </div>
  );
}

export default function CartaoDoExtrato({
  etiqueta,
  tom = 'amarelo',
  data,
  dataEmCaixa,
  contexto,
  titulo,
  extras,
  participacao,
  pontos,
  pontosEmBloco,
  detalhes,
}: CartaoDoExtratoProps) {
  return (
    <article
      className={`flex flex-col gap-4 rounded-2xl bg-surface-0 px-5 py-[22px] text-content shadow-jj-01 ${
        dataEmCaixa
          ? 'lg:px-[26px]'
          : 'transition hover:-translate-y-0.5 hover:shadow-[0_8px_20px_rgba(0,45,75,0.12)] lg:px-[30px]'
      }`}
    >
      <div className="flex flex-wrap items-center gap-5" tabIndex={0}>
        {dataEmCaixa ? <CaixaDeData time={dataEmCaixa} /> : null}

        <div className="min-w-[200px] flex-1">
          {(etiqueta || data) && (
            <div className="mb-[7px] flex flex-wrap items-center gap-2.5">
              {etiqueta && (
                <span
                  className={`${COR_DA_ETIQUETA[tom]} rounded-jj-pill px-[13px] py-[5px] font-display text-[11px] font-bold uppercase tracking-[0.05em] text-brand-blue-dark`}
                >
                  {etiqueta}
                </span>
              )}
              {data ? <span className="text-[12.5px] text-content-soft">{format(new Date(data), 'dd/MM/yyyy')}</span> : null}
            </div>
          )}
          {contexto && <p className="m-0 mb-1.5 text-xs text-content-soft">{contexto}</p>}
          {titulo && (
            <div
              className={`m-0 leading-[1.45] text-content ${
                dataEmCaixa ? 'font-text text-[15px] font-normal' : 'font-display text-base font-bold'
              }`}
            >
              {titulo}
            </div>
          )}
          {extras && <div className="mt-2.5 flex flex-wrap items-center gap-2">{extras}</div>}
        </div>

        <div className="flex shrink-0 items-center gap-2.5">
          {participacao ? (
            <span className="rounded-jj-pill bg-brand-cyan-light px-4 py-2.5 font-display text-sm font-bold text-content">
              {participacao}%
            </span>
          ) : null}
          {pontos === undefined ? null : pontosEmBloco ? (
            <span className="rounded-jj-pill bg-brand-blue px-5 py-[9px] text-center font-display text-[15px] font-bold leading-[1.05] text-brand-white">
              {FormatNumber(pontos, false)}
              <span className="block font-text text-[9px] font-normal uppercase tracking-[0.05em]">Pontos</span>
            </span>
          ) : (
            <span className="whitespace-nowrap rounded-jj-pill bg-brand-blue px-[18px] py-2 font-display text-sm font-bold text-brand-white">
              {FormatNumber(pontos, false)} pontos
            </span>
          )}
        </div>
      </div>

      {detalhes && (
        <div className="border-t border-[rgba(0,45,75,0.14)] pt-4 text-sm leading-normal text-content">{detalhes}</div>
      )}
    </article>
  );
}
